import {FC, useEffect, useMemo} from 'react';
import {companyApi} from 'src/api';
import {AMPAutocomplete} from 'src/components/common/AMPAutocomplete';
import {FormikProps, useFormikContext} from 'formik';
import {AMPAutocompleteProps} from 'src/components/common/AMPAutocomplete/AMPAutocomplete.types';
import {getHelperText} from 'src/utils/forms';
import {useIntl} from 'react-intl';
import {CreateRfidFormFromType} from '..';
import {useBusinessId, useAppSelector} from 'src/app/hooks';
import {CompanySearchRequest} from 'src/types';

export const CompanyForRfidDropdown: FC = () => {
    const intl = useIntl();

    const {authInfo} = useAppSelector((state) => state.common);
    const businessId = useBusinessId();
    const {setFieldValue, handleBlur, touched, errors, values} =
        useFormikContext<CreateRfidFormFromType>();

    // The business being worked in, for everyone. The administrator used to get every tenant's
    // companies in a dropdown that decides which company a tag is issued to.
    const companyRequestBody = (): Pick<CompanySearchRequest, 'data'> => ({
        data: {businessId},
    });

    const {data: companyData, refetch: companyRefetch} = companyApi.useSearchCompaniesQuery({
        params: {
            page: 0,
            size: 1_000,
        },
        ...companyRequestBody(),
    });

    const options = useMemo(() => {
        return (
            companyData?.content?.map((company) => ({
                id: Number(company?.id) ?? 0,
                label: company?.name ?? '',
            })) ?? []
        );
    }, [companyData?.content]);

    const handleChangeCompanyValue = (
        setFieldValue: FormikProps<CreateRfidFormFromType>['setFieldValue'],
        fieldName: string
    ): AMPAutocompleteProps['onChange'] => {
        return (_event, newValue) => {
            // Allow clearing the field by setting to null when newValue is null/undefined
            setFieldValue(fieldName, newValue ? newValue.id : null);
        };
    };

    useEffect(() => {
        companyRefetch();
    }, []);

    return (
        <AMPAutocomplete
            options={options}
            value={options.find((item) => item.id === Number(values['companyId']))}
            label={'companyTabForRfidDialog'}
            input={'companyId'}
            showAvatar
            onChange={handleChangeCompanyValue(setFieldValue, 'companyId')}
            onBlur={handleBlur}
            hasError={
                (touched?.['companyId'] && Boolean(errors['companyId'])) ||
                (touched?.['companyId'] && values['companyId'] === 0)
            }
            helperText={getHelperText('companyId', touched, errors, intl)}
        />
    );
};
